import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { validateEmail, validateName, validatePhone } from '../../utils/validation';
import api from '../../utils/api';
import './ReservationForm.css';

function formatTime(time24) {
  const [hourStr] = time24.split(':');
  const hour = parseInt(hourStr);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour > 12 ? hour - 12 : hour;
  return `${hour12}:00 ${period}`;
}

function ReservationForm() {
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone_number: '',
    num_guests: 2,
    date: null,
    time: '',
  });
  const [availability, setAvailability] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (formData.date) {
      // Use local date formatting to avoid UTC date shift
      const year = formData.date.getFullYear();
      const month = String(formData.date.getMonth() + 1).padStart(2, '0');
      const day = String(formData.date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      setLoadingSlots(true);
      setAvailability({});
      api.get(`/availability?date=${dateStr}`)
        .then((res) => setAvailability(res.data))
        .catch(() => setAvailability({}))
        .finally(() => setLoadingSlots(false));
    }
  }, [formData.date]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!validateName(formData.customer_name)) {
      newErrors.customer_name = 'Name must be at least 2 characters.';
    }
    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!validatePhone(formData.phone_number)) {
      newErrors.phone_number = 'Please enter a valid phone number.';
    }
    if (!formData.date) {
      newErrors.date = 'Please select a date.';
    }
    if (!formData.time) {
      newErrors.time = 'Please select a time slot.';
    }
    if (!formData.num_guests || formData.num_guests < 1) {
      newErrors.num_guests = 'At least 1 guest is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setResult(null);

    const year = formData.date.getFullYear();
    const month = String(formData.date.getMonth() + 1).padStart(2, '0');
    const day = String(formData.date.getDate()).padStart(2, '0');
    const hours = formData.time.split(':')[0].padStart(2, '0');
    const timeSlotStr = `${year}-${month}-${day}T${hours}:00:00`;

    try {
      const response = await api.post('/reservations', {
        customer_name: formData.customer_name,
        email: formData.email,
        phone_number: formData.phone_number || null,
        num_guests: formData.num_guests,
        time_slot: timeSlotStr,
      });
      setResult({ type: 'success', data: response.data });
    } catch (err) {
      const message = err.response?.data?.error || 'Something went wrong. Please try again.';
      setResult({ type: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (result?.type === 'success') {
    return (
      <div className="reservation-form__success">
        <div className="reservation-form__success-icon">&#10003;</div>
        <h3>Reservation Confirmed!</h3>
        <div className="reservation-form__success-details">
          <p><strong>Name:</strong> {result.data.customer_name}</p>
          <p><strong>Date & Time:</strong> {new Date(result.data.time_slot).toLocaleString()}</p>
          <p><strong>Table:</strong> #{result.data.table_number}</p>
          <p><strong>Guests:</strong> {result.data.num_guests}</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            setResult(null);
            setFormData({
              customer_name: '',
              email: '',
              phone_number: '',
              num_guests: 2,
              date: null,
              time: '',
            });
          }}
        >
          Make Another Reservation
        </button>
      </div>
    );
  }

  return (
    <form className="reservation-form" onSubmit={handleSubmit}>
      {result?.type === 'error' && (
        <div className="reservation-form__error-banner">
          {result.message}
        </div>
      )}

      <div className="reservation-form__grid">
        <div className="reservation-form__field">
          <label htmlFor="customer_name">Full Name *</label>
          <input
            type="text"
            id="customer_name"
            name="customer_name"
            value={formData.customer_name}
            onChange={handleChange}
            placeholder="John Doe"
            required
          />
          {errors.customer_name && <span className="reservation-form__field-error">{errors.customer_name}</span>}
        </div>

        <div className="reservation-form__field">
          <label htmlFor="email">Email Address *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            required
          />
          {errors.email && <span className="reservation-form__field-error">{errors.email}</span>}
        </div>

        <div className="reservation-form__field">
          <label htmlFor="phone_number">Phone Number</label>
          <input
            type="tel"
            id="phone_number"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            placeholder="(202) 555-1234"
          />
          {errors.phone_number && <span className="reservation-form__field-error">{errors.phone_number}</span>}
        </div>

        <div className="reservation-form__field">
          <label htmlFor="num_guests">Number of Guests *</label>
          <input
            type="number"
            id="num_guests"
            name="num_guests"
            value={formData.num_guests}
            onChange={handleChange}
            min="1"
            max="10"
            required
          />
          {errors.num_guests && <span className="reservation-form__field-error">{errors.num_guests}</span>}
        </div>

        <div className="reservation-form__field">
          <label>Date *</label>
          <DatePicker
            selected={formData.date}
            onChange={(date) => {
              setFormData((prev) => ({ ...prev, date, time: '' }));
              if (errors.date) setErrors((prev) => ({ ...prev, date: '' }));
            }}
            minDate={tomorrow}
            placeholderText="Select a date"
            dateFormat="MMMM d, yyyy"
            className="reservation-form__datepicker"
          />
          {errors.date && <span className="reservation-form__field-error">{errors.date}</span>}
        </div>

        <div className="reservation-form__field">
          <label htmlFor="time">Time Slot *</label>
          <select
            id="time"
            name="time"
            value={formData.time}
            onChange={handleChange}
            disabled={!formData.date}
            required
          >
            <option value="">
              {!formData.date ? 'Pick a date first' : loadingSlots ? 'Loading available times...' : 'Select a time'}
            </option>
            {Object.entries(availability).map(([time, tables]) => (
              <option key={time} value={time} disabled={tables === 0}>
                {formatTime(time)} ({tables} {tables === 1 ? 'table' : 'tables'} available)
              </option>
            ))}
          </select>
          {errors.time && <span className="reservation-form__field-error">{errors.time}</span>}
        </div>
      </div>

      <button
        type="submit"
        className="btn-primary reservation-form__submit"
        disabled={submitting}
      >
        {submitting ? 'Booking...' : 'Reserve a Table'}
      </button>
    </form>
  );
}

export default ReservationForm;
